/**
 * État et actions de la gestion des pistes, partagés par l'outil 3D
 * (render/piste-tool.ts) et le HUD (pistes.tsx) : verdict de l'aperçu,
 * lancement d'un tracé ou d'une reprise, envoi au serveur.
 *
 * Une reprise ou une démolition refusée parce que des skieurs sont sur la
 * piste (PISTE_BUSY) n'est pas un échec : la piste est fermée, puis la
 * commande repart toutes les 2 s jusqu'à ce qu'elle soit vide.
 */
import { signal } from '@preact/signals';
import { PISTE_BUSY, editablePoints, type Command, type Difficulty, type Piste } from '@snowchain/shared';
import { hud } from './context';
import { euros, n0, n1 } from './format';
import { toast, ui } from './ui-state';

/** Verdict de l'aperçu, affiché dans la barre de tracé. */
export interface PisteView {
  ok: boolean;
  /** Tracé neuf dont l'arrivée ne rejoint encore rien : à prolonger, pas une erreur. */
  pending?: boolean;
  reason?: string;
  cost: number;
  hours?: number;
  difficulty?: Difficulty;
  length?: number;
  drop?: number;
  width?: number;
  /** Ce que rejoignent le départ et l'arrivée. */
  from?: string;
  to?: string;
  /** Ce que ferait un clic à l'endroit survolé. */
  hint?: string;
}

export const pisteView = signal<PisteView | null>(null);

/** Piste dont la démolition attend qu'elle se vide. */
export const demolishWait = signal<number | null>(null);

const RETRY_MS = 2000;
/** Au-delà, on abandonne (la piste reste fermée, le joueur peut relancer). */
const RETRY_MAX = 300;

let editTimer: ReturnType<typeof setTimeout> | null = null;
let demolishTimer: ReturnType<typeof setTimeout> | null = null;
/** Piste fermée par l'outil le temps de la vider : à rouvrir ensuite. */
let closedForEdit: number | null = null;
let closedForDemolish: number | null = null;

const send = (cmd: Command) => hud.store?.sendCommand(cmd) ?? Promise.resolve({ t: 'ack' as const, seq: 0, ok: false, error: 'Hors ligne' });

function ownerOnly(): boolean {
  if (hud.store?.isOwner) return true;
  toast('warn', 'Mode observateur', 'Seul le gérant de la station peut modifier les pistes.');
  return false;
}

/** Ouvre l'outil de tracé d'une piste neuve. */
export function startTrace(): void {
  if (!ownerOnly()) return;
  stopEdit(false);
  ui.selection.value = null;
  ui.build.value = { kind: 'piste', points: [], route: 'bleue', size: 'normal' };
  ui.buildMenu.value = true;
}

/** Ouvre la reprise du tracé d'une piste : ses points de passage deviennent des poignées. */
export function startEdit(p: Piste): void {
  if (!ownerOnly()) return;
  ui.build.value = null;
  ui.buildMenu.value = false;
  stopEdit(false);
  ui.pisteEdit.value = { id: p.id, points: editablePoints(p), route: p.route, size: p.size, waiting: false };
}

/** Quitte la reprise. `done` : la reprise est partie, la piste rouvrira après ses travaux. */
export function stopEdit(done: boolean): void {
  if (editTimer) clearTimeout(editTimer);
  editTimer = null;
  const id = closedForEdit;
  closedForEdit = null;
  // Fermée pour la vider : on la rouvre (après la reprise, ses travaux la gardent fermée).
  if (id !== null) void send({ type: 'setPisteOpen', id, open: true });
  if (!done && ui.pisteEdit.value === null) return;
  ui.pisteEdit.value = null;
  pisteView.value = null;
}

/** Valide le tracé en cours (piste neuve) ou la reprise. */
export async function submitPiste(): Promise<void> {
  if (!ownerOnly()) return;
  const b = ui.build.value;
  if (b?.kind === 'piste') {
    const v = pisteView.value;
    if (b.points.length < 2) {
      toast('warn', 'Tracé incomplet', 'Placez un départ, puis une arrivée.');
      return;
    }
    if (v && !v.ok) {
      toast('warn', 'Tracé impossible', v.reason);
      return;
    }
    const ack = await send({ type: 'buildPiste', points: b.points, route: b.route, size: b.size });
    if (!ack.ok) {
      toast('danger', 'Tracé refusé', ack.error);
      return;
    }
    toast('ok', 'Travaux lancés', v ? `${euros(v.cost)} · ouverture dans ${n1(v.hours ?? 0)} h de jeu` : undefined);
    // L'outil reste ouvert pour tracer la suivante.
    ui.build.value = { ...b, points: [] };
    return;
  }
  const e = ui.pisteEdit.value;
  if (!e) return;
  const v = pisteView.value;
  if (v && !v.ok) {
    toast('warn', 'Tracé impossible', v.reason);
    return;
  }
  await trySubmitEdit(0);
}

async function trySubmitEdit(attempt: number): Promise<void> {
  editTimer = null;
  const e = ui.pisteEdit.value;
  const store = hud.store;
  const p = e ? store?.replica?.pistes.get(e.id) : undefined;
  if (!e || !store || !p) {
    stopEdit(true);
    return;
  }
  const cost = pisteView.value?.cost ?? 0;
  const ack = await send({ type: 'editPiste', id: e.id, points: e.points, route: e.route, size: e.size });
  // Annulée pendant l'attente : rien à faire.
  if (ui.pisteEdit.value?.id !== e.id) return;
  if (ack.ok) {
    toast('ok', `Reprise de ${p.name}`, `Travaux lancés : ${euros(cost)}`);
    stopEdit(true);
    return;
  }
  if (ack.error === PISTE_BUSY && attempt < RETRY_MAX) {
    if (!p.manualClosed && closedForEdit === null) {
      closedForEdit = e.id;
      await send({ type: 'setPisteOpen', id: e.id, open: false });
    }
    if (!ui.pisteEdit.value.waiting) ui.pisteEdit.value = { ...ui.pisteEdit.value, waiting: true };
    editTimer = setTimeout(() => void trySubmitEdit(attempt + 1), RETRY_MS);
    return;
  }
  ui.pisteEdit.value = { ...ui.pisteEdit.value, waiting: false };
  toast('danger', 'Reprise refusée', ack.error);
}

/** Démolit une piste ; occupée, elle est fermée puis démolie dès qu'elle est vide. */
export async function demolishPiste(id: number, attempt = 0): Promise<void> {
  if (attempt === 0 && !ownerOnly()) return;
  demolishTimer = null;
  const store = hud.store;
  const p = store?.replica?.pistes.get(id);
  if (!store || !p) {
    demolishWait.value = null;
    return;
  }
  if (attempt > 0 && demolishWait.value !== id) return;
  const ack = await send({ type: 'demolish', id });
  if (ack.ok) {
    closedForDemolish = null;
    demolishWait.value = null;
    if (ui.selection.value?.kind === 'piste' && ui.selection.value.id === id) ui.selection.value = null;
    toast('ok', 'Piste démolie', p.name);
    return;
  }
  if (ack.error === PISTE_BUSY && attempt < RETRY_MAX) {
    if (!p.manualClosed && closedForDemolish !== id) {
      closedForDemolish = id;
      await send({ type: 'setPisteOpen', id, open: false });
    }
    if (attempt === 0) toast('info', `Fermeture de ${p.name}`, 'La piste sera démolie dès que les skieurs l\u2019auront quittée.');
    demolishWait.value = id;
    demolishTimer = setTimeout(() => void demolishPiste(id, attempt + 1), RETRY_MS);
    return;
  }
  demolishWait.value = null;
  toast('danger', 'Démolition refusée', ack.error);
}

/** Renonce à une démolition en attente (la piste rouvre si l'outil l'avait fermée). */
export function cancelDemolish(): void {
  if (demolishTimer) clearTimeout(demolishTimer);
  demolishTimer = null;
  const id = closedForDemolish;
  closedForDemolish = null;
  demolishWait.value = null;
  if (id !== null) void send({ type: 'setPisteOpen', id, open: true });
}

/** Ligne de résumé d'une piste : longueur, dénivelé, largeur. */
export function pisteFacts(p: Pick<Piste, 'length' | 'drop' | 'width'>): string {
  return `${n0(p.length)} m · ${n0(p.drop)} m de dénivelé · ${n0(p.width)} m de large`;
}
